/**
 * DecisionFlow — PlanAssignmentPanel
 * ui/src/components/admin/PlanAssignmentPanel.jsx
 *
 * S833 Steps 4+5 — AssignmentDecisionGate + AssignmentCsvImport +
 * AssignmentPositionPicker with IBM dual-panel pattern.
 *
 * Architecture:
 *   AssignmentDecisionGate   — shared decision gate (§4.1), used by both paths
 *   AssignmentCsvImport      — CSV governed flow (Step 4)
 *   AssignmentPositionPicker — dual-panel position picker (Step 5)
 *   AssignmentTable          — current assignments table (Step 3)
 *   PlanAssignmentPanel      — tab shell mounting all of the above
 *
 * Backend endpoints:
 *   GET  /plan-assignments/analyze        — population pre-flight
 *   GET  /plan-assignments/conflicts      — 15-category conflict detection
 *   POST /plan-assignments/bulk-import    — CSV file path (FormData)
 *   POST /plan-assignments/bulk-import-json — position picker path (JSON body)
 *   GET  /plan-assignments                — current assignments list
 *   GET  /plans                           — approved plan list for picker
 *
 * OI-S822: segment_name used for display (role_code JOIN fix deferred S834)
 * OI-S833-JSON-BODY: position picker uses /bulk-import-json (JSON body)
 * S832-DECISION-GATE: dry run always force=true to surface supersessions
 * S832-PARTIAL-LOAD-NOT-ALL-OR-NOTHING: partial load, never all-or-nothing
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Search, Lock, ChevronUp, ChevronDown, ChevronsUpDown, X,
  ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft,
  AlertTriangle, CheckCircle2, AlertCircle, Copy, RefreshCw,
  Filter, Loader2, ShieldCheck, Info,
} from "lucide-react";

import Card from "../shared/Card.jsx";
import Pill from "../shared/Pill.jsx";

const API_BASE = "/api/v1";

// ── Local utilities ──────────────────────────────────────────────────────────

function useFetch(url) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  useEffect(() => {
    if (!url) { setLoading(false); return; }
    setLoading(true); setData(null); setError(null);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);
  return { data, loading, error };
}

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2 py-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />;
  return sortDir === "asc"
    ? <ChevronUp   className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
    : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />;
}

function thCls() {
  return "px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]";
}
function tdCls() {
  return "px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap";
}

function Spinner({ size = 4 }) {
  return <Loader2 className={`h-${size} w-${size} animate-spin text-[rgb(var(--color-primary))]`} />;
}

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

// ── AssignmentDecisionGate ───────────────────────────────────────────────────
// Shared between CSV path and position picker path. Five states per spec §4.1.
// Props: summary (dry run result), onOptionA, onOptionB, onCancel

function AssignmentDecisionGate({ summary, onOptionA, onOptionB, onCancel, committing }) {
  const X = summary.assignments_would_create   ?? 0;
  const Y = summary.assignments_would_supersede ?? 0;
  const Z = summary.errors?.length             ?? 0;
  const warnings = summary.warnings            ?? [];

  // Unique existing plan codes from warnings for acknowledgment line
  const existingCodes = [...new Set(warnings.map((w) => w.existing_plan_code).filter(Boolean))];

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Review Assignment</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
          {summary.validated} position{summary.validated !== 1 ? "s" : ""} validated
        </p>
      </div>

      {/* Summary buckets */}
      <div className="grid grid-cols-3 divide-x divide-[rgb(var(--color-border))] border-b border-[rgb(var(--color-border))]">
        <div className="px-4 py-3 text-center">
          <p className="text-tok-xl font-semibold text-[rgb(var(--color-success))]">{X}</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Ready</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className={`text-tok-xl font-semibold ${Y > 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text-muted))]"}`}>{Y}</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Will Supersede</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className={`text-tok-xl font-semibold ${Z > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-text-muted))]"}`}>{Z}</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Errors</p>
        </div>
      </div>

      {/* Error table — shown when Z > 0 */}
      {Z > 0 && (
        <div className="px-4 py-3 border-b border-[rgb(var(--color-border))]">
          <p className="text-tok-xs font-semibold text-[rgb(var(--color-danger))] mb-2">
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
            {Z} row{Z !== 1 ? "s" : ""} have errors — review before committing
          </p>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))]">
            <table className="w-full text-tok-xs">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                <tr>
                  {["Row","Employee ID","Field","Message"].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(summary.errors ?? []).slice(0, 50).map((e, i) => (
                  <tr key={i} className="border-t border-[rgb(var(--color-border))]">
                    <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{e.row ?? "—"}</td>
                    <td className="px-2 py-1 font-mono text-[rgb(var(--color-text))]">{e.employee_id ?? "—"}</td>
                    <td className="px-2 py-1 font-mono text-[rgb(var(--color-warning))]">{e.field ?? "—"}</td>
                    <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{e.message ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {Z > 0 && (
            <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Re-upload to fix errors. Already-loaded positions will not be duplicated.
            </p>
          )}
        </div>
      )}

      {/* Option B acknowledgment line */}
      {Y > 0 && (
        <div className="px-4 py-3 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-warning))]/5">
          <p className="text-tok-xs text-[rgb(var(--color-warning))]">
            <AlertCircle className="inline h-3.5 w-3.5 mr-1" />
            {Y} existing assignment{Y !== 1 ? "s" : ""} will be replaced
            {existingCodes.length > 0 && (
              <span className="ml-1 font-mono">({existingCodes.join(", ")})</span>
            )}
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="px-4 py-3 flex flex-wrap items-center gap-2">
        {/* State A: clean — one button */}
        {X > 0 && Y === 0 && Z === 0 && (
          <button
            className="primary-button"
            disabled={committing}
            onClick={onOptionA}
          >
            {committing ? <Spinner size={3} /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            Assign {X} position{X !== 1 ? "s" : ""}
          </button>
        )}

        {/* State B: clean + supersessions — two buttons */}
        {X > 0 && Y > 0 && Z === 0 && (
          <>
            <button className="primary-button" disabled={committing} onClick={onOptionA}>
              {committing ? <Spinner size={3} /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Assign {X}, skip {Y} conflict{Y !== 1 ? "s" : ""}
            </button>
            <button
              className="secondary-button border-[rgb(var(--color-warning))]/50 text-[rgb(var(--color-warning))] hover:bg-[rgb(var(--color-warning))]/8"
              disabled={committing}
              onClick={onOptionB}
            >
              {committing ? <Spinner size={3} /> : <AlertCircle className="h-3.5 w-3.5" />}
              Assign all — replace {Y} existing
            </button>
          </>
        )}

        {/* State C: errors + some valid */}
        {X > 0 && Z > 0 && (
          <>
            <button className="primary-button" disabled={committing} onClick={onOptionA}>
              {committing ? <Spinner size={3} /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Assign {X} valid, skip {Z} error{Z !== 1 ? "s" : ""}
            </button>
            {Y > 0 && (
              <button
                className="secondary-button border-[rgb(var(--color-warning))]/50 text-[rgb(var(--color-warning))]"
                disabled={committing}
                onClick={onOptionB}
              >
                Assign {X} + replace {Y}, skip {Z} error{Z !== 1 ? "s" : ""}
              </button>
            )}
          </>
        )}

        {/* State D/E: nothing to do or all errors */}
        {X === 0 && Y === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No new assignments found. All positions are either already assigned, inactive, or have data errors.
          </p>
        )}

        <button
          className="secondary-button ml-auto"
          disabled={committing}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── AssignmentResultCard ─────────────────────────────────────────────────────
// Shown after a successful commit. Four buckets + import log ID.

function AssignmentResultCard({ result, onReset }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    copyToClipboard(result.import_log_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      <div className="px-4 py-3 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] flex items-center justify-between">
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Assignment Complete</p>
        <button className="secondary-button text-tok-xs py-1" onClick={onReset}>
          <RefreshCw className="h-3 w-3" /> New Assignment
        </button>
      </div>
      <div className="px-4 py-3 space-y-2">
        {result.assignments_created > 0 && (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
            <span className="text-tok-sm text-[rgb(var(--color-text))]">
              <strong className="text-[rgb(var(--color-success))]">{result.assignments_created}</strong> new assignments created
            </span>
          </div>
        )}
        {(result.assignments_superseded ?? 0) > 0 && (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0" />
            <span className="text-tok-sm text-[rgb(var(--color-text))]">
              <strong className="text-[rgb(var(--color-warning))]">{result.assignments_superseded}</strong> existing assignments superseded
            </span>
          </div>
        )}
        {(result.errors?.length ?? 0) > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
            <span className="text-tok-sm text-[rgb(var(--color-text))]">
              <strong className="text-[rgb(var(--color-danger))]">{result.errors.length}</strong> rows rejected — fix and re-upload
            </span>
          </div>
        )}
        {result.import_log_id && (
          <div className="flex items-center gap-2 pt-1 border-t border-[rgb(var(--color-border))]">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Import log:</span>
            <span className="text-tok-xs font-mono text-[rgb(var(--color-text))] truncate max-w-xs">{result.import_log_id}</span>
            <button className="icon-button h-5 w-5" onClick={handleCopy} title="Copy log ID">
              <Copy className="h-3 w-3" />
            </button>
            {copied && <span className="text-tok-xs text-[rgb(var(--color-success))]">Copied</span>}
          </div>
        )}
      </div>
      {(result.errors?.length ?? 0) > 0 && (
        <div className="px-4 pb-3">
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-2">
            Re-upload the full file after fixing the {result.errors.length} rejected row{result.errors.length !== 1 ? "s" : ""}. Already-loaded assignments will not be duplicated.
          </p>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))]">
            <table className="w-full text-tok-xs">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                <tr>{["Row","Field","Message"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {result.errors.slice(0, 50).map((e, i) => (
                  <tr key={i} className="border-t border-[rgb(var(--color-border))]">
                    <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{e.row ?? "—"}</td>
                    <td className="px-2 py-1 font-mono text-[rgb(var(--color-warning))]">{e.field ?? "—"}</td>
                    <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{e.message ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AssignmentCsvImport ──────────────────────────────────────────────────────
// States: IDLE → VALIDATING → DECIDING → COMMITTING → DONE
// Always calls dry_run=true&force=true so supersessions surface as warnings.

function AssignmentCsvImport({ period, onSuccess }) {
  const [state, setState]       = useState("IDLE");   // IDLE|VALIDATING|DECIDING|COMMITTING|DONE|ERROR
  const [csvFile, setCsvFile]   = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const periodLocked = period?.period_status !== "open";

  function reset() {
    setState("IDLE");
    setCsvFile(null);
    setDryResult(null);
    setCommitResult(null);
    setErrorMsg("");
  }

  async function handleValidate(file) {
    if (!file) return;
    setState("VALIDATING");
    setErrorMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=true&force=true`, {
        method: "POST", credentials: "include", body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorMsg(json?.detail?.error?.message ?? json?.detail ?? `Error ${res.status}`);
        setState("ERROR");
        return;
      }
      setDryResult(json);
      setState("DECIDING");
    } catch (e) {
      setErrorMsg(e.message || "Validation request failed.");
      setState("ERROR");
    }
  }

  async function handleCommit(force) {
    if (!csvFile) return;
    setState("COMMITTING");
    setErrorMsg("");
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const url = `${API_BASE}/plan-assignments/bulk-import?dry_run=false&force=${force}`;
      const res = await fetch(url, { method: "POST", credentials: "include", body: fd });
      const json = await res.json().catch(() => ({}));
      // S931 OI-S909: Router returns 207 (partial) or 422 (all-rejected) with the same
      // body shape: { validated, errors, assignments_created, import_log_id }.
      // AssignmentResultCard handles all three outcomes (full/partial/all-rejected)
      // via assignments_created count + errors array — always route to DONE.
      // Only treat as a hard ERROR for non-assignment failures (network, auth, 500s).
      if (!res.ok && res.status !== 207 && res.status !== 422) {
        setErrorMsg(json?.detail?.error?.message ?? json?.detail ?? `Error ${res.status}`);
        setState("ERROR");
        return;
      }
      setCommitResult(json);
      setState("DONE");
      onSuccess?.();
    } catch (e) {
      setErrorMsg(e.message || "Commit failed.");
      setState("ERROR");
    }
  }

  if (periodLocked) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
        <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
        <p className="text-tok-sm text-[rgb(var(--color-warning))]">
          Period is <strong>{period?.period_status ?? "not open"}</strong> — assignment imports are not available.
        </p>
      </div>
    );
  }

  if (state === "DONE" && commitResult) {
    return <AssignmentResultCard result={commitResult} onReset={reset} />;
  }

  return (
    <div className="space-y-3">
      {/* Instructions */}
      {state === "IDLE" && (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">
            Upload <strong>PlanAssignments.csv</strong> to assign positions to plans.
          </p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-3">
            Required: <span className="text-[rgb(var(--color-primary))]">employee_id, plan_code, fiscal_period_code, business_unit_code, effective_date</span>
            <span className="ml-2">· Optional: end_date, plan_version <span className="text-[rgb(var(--color-text-muted))] font-sans not-italic">(integer — targets a specific approved version; omit to use governing)</span></span>
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Re-upload is safe — already-assigned positions are not duplicated.
          </p>
        </div>
      )}

      {/* File picker */}
      {(state === "IDLE" || state === "ERROR") && (
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".csv"
            className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setCsvFile(f);
              setErrorMsg("");
            }}
          />
          <button
            className="primary-button"
            disabled={!csvFile}
            onClick={() => handleValidate(csvFile)}
          >
            Validate
          </button>
        </div>
      )}

      {/* Error message */}
      {errorMsg && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
          {errorMsg}
          <button className="ml-3 text-tok-xs underline" onClick={reset}>Try again</button>
        </div>
      )}

      {/* Validating spinner */}
      {state === "VALIDATING" && (
        <div className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          <Spinner /> Validating rows against current assignments…
        </div>
      )}

      {/* Committing spinner */}
      {state === "COMMITTING" && (
        <div className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          <Spinner /> Assigning positions…
        </div>
      )}

      {/* Decision gate */}
      {state === "DECIDING" && dryResult && (
        <AssignmentDecisionGate
          summary={dryResult}
          onOptionA={() => handleCommit(false)}
          onOptionB={() => handleCommit(true)}
          onCancel={reset}
          committing={false}
        />
      )}
    </div>
  );
}

// ── PopulationAnalysisPanel ─────────────────────────────────────────────────
// Ported from CompensationPoliciesWorkspace.jsx §6. Self-contained, no imports needed.
// Health badge: healthy | attention_required | unassigned

function HealthBadge({ status }) {
  const cfg = {
    healthy:            { label: "Healthy",            cls: "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]" },
    attention_required: { label: "Attention Required", cls: "border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]" },
    unassigned:         { label: "Unassigned",         cls: "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]" },
  };
  const { label, cls } = cfg[status] ?? cfg.unassigned;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function PopulationAnalysisPanel({ fiscalPeriodId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    if (!fiscalPeriodId) return;
    setLoading(true); setError(null);
    fetch(`${API_BASE}/plan-assignments/analyze?fiscal_period_id=${fiscalPeriodId}`, { credentials: "include" })
      .then((r) => { if (!r.ok) return r.json().then((b) => { throw new Error(b?.detail?.error?.message ?? `HTTP ${r.status}`); }); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message ?? "Unable to load population analysis."))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId]);

  useEffect(() => { load(); }, [load]);

  const summary    = data?.summary    ?? null;
  const components = data?.components ?? [];
  const hasData    = summary || components.length > 0;

  return (
    <Card
      title="Population Analysis"
      action={
        <button onClick={load} disabled={loading || !fiscalPeriodId}
          className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-40">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      }
    >
      {!fiscalPeriodId && <ErrorNote message="No open fiscal period." />}
      {fiscalPeriodId && loading && <LoadingRow />}
      {fiscalPeriodId && error   && <ErrorNote message={error} />}
      {fiscalPeriodId && !loading && !error && !hasData && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No assignment data for this period.</p>
      )}
      {fiscalPeriodId && !loading && !error && hasData && (
        <div className="space-y-4">
          {summary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                ["Matched",          summary.matched],
                ["Already Assigned", summary.already_assigned],
                ["Inactive",         summary.inactive],
                ["Pending Hire",     summary.pending_hire],
                ["Needs Review",     summary.needs_review],
                ["Ready",            summary.ready],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-3 text-center">
                  <p className="text-[18px] font-semibold text-[rgb(var(--color-text))]">{value ?? "—"}</p>
                  <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</p>
                </div>
              ))}
            </div>
          )}
          {components.length > 0 && (
            <div className="space-y-2">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">By Plan Component</p>
              <div className="overflow-x-auto overflow-y-auto max-h-72 rounded-xl border border-[rgb(var(--color-border))]">
                <table className="w-full text-tok-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                      {["Plan / Component","Assigned","Ready","Health"].map((h) => (
                        <th key={h} className="px-4 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {components.map((comp, idx) => (
                      <tr key={comp.plan_component_id ?? idx} className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className="px-4 py-3">
                          <p className="font-medium text-[rgb(var(--color-text))]">{comp.role_name ?? comp.component_label ?? "—"}</p>
                          {comp.component_label && comp.role_name && (
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{comp.component_label}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-[rgb(var(--color-text))]">{comp.already_assigned ?? "—"}</td>
                        <td className="px-4 py-3 font-mono text-[rgb(var(--color-text))]">{comp.ready ?? "—"}</td>
                        <td className="px-4 py-3">
                          <HealthBadge status={
                            comp.already_assigned === comp.matched && comp.matched > 0 ? "healthy"
                            : comp.ready > 0 ? "attention_required"
                            : "unassigned"
                          } />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── ConflictDetectionPanel ────────────────────────────────────────────────────
// Ported from CompensationPoliciesWorkspace.jsx §6. On-demand via button.

function ConflictDetectionPanel({ fiscalPeriodId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [checked, setChecked] = useState(false);

  function runCheck() {
    if (!fiscalPeriodId) return;
    setLoading(true); setError(null); setData(null); setChecked(false);
    fetch(`${API_BASE}/plan-assignments/conflicts?fiscal_period_id=${fiscalPeriodId}`, { credentials: "include" })
      .then((r) => { if (!r.ok) return r.json().then((b) => { throw new Error(b?.detail?.error?.message ?? `HTTP ${r.status}`); }); return r.json(); })
      .then((d) => { setData(d); setChecked(true); })
      .catch((e) => { setError(e.message ?? "Unable to check conflicts."); setChecked(true); })
      .finally(() => setLoading(false));
  }

  const rawConflicts  = data?.conflicts ?? [];
  const totalErrors   = data?.total_errors   ?? rawConflicts.filter((c) => c.severity === "error").length;
  const totalWarnings = data?.total_warnings ?? rawConflicts.filter((c) => c.severity === "warning").length;

  // Group flat list by conflict_category
  const grouped = useMemo(() => {
    const map = {};
    rawConflicts.forEach((c) => {
      const cat = c.conflict_category ?? "Other";
      if (!map[cat]) map[cat] = { category: cat, conflicts: [] };
      map[cat].conflicts.push(c);
    });
    return Object.values(map);
  }, [rawConflicts]);

  return (
    <Card
      title="Conflict Detection"
      action={checked && (totalErrors + totalWarnings) > 0 ? (
        <div className="flex items-center gap-2">
          {totalErrors > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-2.5 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
              <AlertTriangle className="h-3 w-3" />{totalErrors} error{totalErrors !== 1 ? "s" : ""}
            </span>
          )}
          {totalWarnings > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-2.5 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
              <AlertTriangle className="h-3 w-3" />{totalWarnings} warning{totalWarnings !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      ) : null}
    >
      <p className="mb-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        15-category deterministic conflict detection. No AI — all rule-based.
      </p>
      <div className="mb-4">
        <button onClick={runCheck} disabled={loading || !fiscalPeriodId}
          className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""} text-[rgb(var(--color-text-muted))]`} />
          {loading ? "Checking…" : "Check for Conflicts"}
        </button>
      </div>
      {error && <ErrorNote message={error} />}
      {!checked && !loading && !error && (
        <div className="rounded-lg border border-dashed border-[rgb(var(--color-border))] px-4 py-5 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Click "Check for Conflicts" to run the 15-category validation.</p>
        </div>
      )}
      {checked && !loading && !error && grouped.length === 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
            <div>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">No conflicts detected</p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-success))]/80">All 15 categories checked — 0 errors, 0 warnings.</p>
            </div>
          </div>
        </div>
      )}
      {checked && !loading && grouped.length > 0 && (
        <div className="space-y-5">
          {grouped.map((group, gIdx) => {
            const gConflicts = group.conflicts ?? [];
            const gLabel     = group.category?.replace(/_/g, " ") ?? `Category ${gIdx + 1}`;
            const gErrors    = gConflicts.filter((c) => c.severity === "error").length;
            const gWarnings  = gConflicts.filter((c) => c.severity === "warning").length;
            return (
              <div key={group.category ?? gIdx}>
                <div className="mb-2 flex items-center gap-2">
                  <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">{gLabel}</p>
                  {gErrors   > 0 && <span className="text-tok-xs text-[rgb(var(--color-danger))]">{gErrors} error{gErrors !== 1 ? "s" : ""}</span>}
                  {gWarnings > 0 && <span className="text-tok-xs text-[rgb(var(--color-warning))]">{gWarnings} warning{gWarnings !== 1 ? "s" : ""}</span>}
                </div>
                <div className="space-y-2">
                  {[...gConflicts].sort((a, b) => (a.severity === "error" ? -1 : 1) - (b.severity === "error" ? -1 : 1)).map((conflict, cIdx) => {
                    const isErr = conflict.severity === "error";
                    return (
                      <div key={cIdx} className={`rounded-lg border px-4 py-3 ${isErr ? "border-[rgb(var(--color-danger))]/25 bg-[rgb(var(--color-danger))]/5" : "border-[rgb(var(--color-warning))]/25 bg-[rgb(var(--color-warning))]/5"}`}>
                        <div className="flex items-start gap-3">
                          <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${isErr ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-warning))]"}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-tok-sm font-semibold ${isErr ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-warning))]"}`}>
                              {conflict.conflict_category?.replace(/_/g, " ") ?? "Conflict"}
                            </p>
                            {conflict.detail && <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">{conflict.detail}</p>}
                            {conflict.resolution && <p className={`mt-1.5 text-tok-xs font-medium ${isErr ? "text-[rgb(var(--color-danger))]/80" : "text-[rgb(var(--color-warning))]/80"}`}>Resolution: {conflict.resolution}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── AssignmentPositionPicker — Shuttle pattern ────────────────────────────────
// S834 rebuild: shuttle UX (left available → ›/‹ → right selected).
// Data source: /analyze scoped to selected plan (never unbounded positions fetch).
// Plan-first gate: nothing shown until a plan is selected.
// Filters (client-side against analyze population, not full org):
//   BU · Role · Segment · Selling Motion · Active only · Group by · Search
// Scale: analyze endpoint returns role+segment scoped population (e.g. 72 of 30k).

function AssignmentPositionPicker({ period, onSuccess }) {
  const periodLocked = period?.period_status !== "open";

  // ── Plan selection ────────────────────────────────────────────────────────
  const [planSearch,   setPlanSearch]   = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDropOpen, setPlanDropOpen] = useState(false);
  const planDropRef = useRef(null);

  const { data: plansData } = useFetch(`${API_BASE}/plans/registry?plan_status=approved&limit=200`);
  const allPlans = plansData?.plans ?? plansData?.data ?? [];

  // ── Filter state — declared BEFORE filteredPlans memo (temporal dependency) ──
  // filterRole / filterSeg hold the DISPLAY NAME (roleName/segmentName from plans registry)
  // for narrowing the plan dropdown. After analyze runs, we derive the matching
  // role_code / segment_code from allPositions for position-level filtering.
  const [filterBU,     setFilterBU]     = useState("");
  const [filterRole,   setFilterRole]   = useState("");   // roleName — narrows plan list
  const [filterSeg,    setFilterSeg]    = useState("");   // segmentName — narrows plan list
  const [filterMotion, setFilterMotion] = useState("");   // motion_code — narrows position list
  const [activeOnly,   setActiveOnly]   = useState(true);
  const [groupMode,    setGroupMode]    = useState("manager");
  const [posSearch,    setPosSearch]    = useState("");

  const hasFilters = filterBU || filterRole || filterSeg || filterMotion;
  function clearFilters() { setFilterBU(""); setFilterRole(""); setFilterSeg(""); setFilterMotion(""); }

  // Plan filter options (from approved plans list, for the always-visible filter bar)
  const planRoleOptions = useMemo(() => [...new Set(allPlans.map((p) => p.roleName).filter(Boolean))].sort(), [allPlans]);
  const planSegOptions  = useMemo(() => [...new Set(allPlans.map((p) => p.segmentName).filter(Boolean))].sort(), [allPlans]);

  // filteredPlans: Role + Segment filters narrow the plan dropdown before selection.
  // A comp admin working AE_ENT segment picks that first → plan list collapses to AE_ENT plans only.
  const filteredPlans = useMemo(() => {
    const q = planSearch.toLowerCase();
    return allPlans.filter((p) => {
      if (filterRole && p.roleName    !== filterRole)    return false;
      if (filterSeg  && p.segmentName !== filterSeg)     return false;
      if (!q) return true;
      return (
        (p.planName ?? "").toLowerCase().includes(q) ||
        (p.planCode ?? "").toLowerCase().includes(q) ||
        (p.roleName ?? "").toLowerCase().includes(q) ||
        (p.segmentName ?? "").toLowerCase().includes(q)
      );
    });
  }, [allPlans, planSearch, filterRole, filterSeg]);

  useEffect(() => {
    function handler(e) { if (planDropRef.current && !planDropRef.current.contains(e.target)) setPlanDropOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function selectPlan(p) { setSelectedPlan(p); setPlanDropOpen(false); setPlanSearch(""); resetGate(); setRightItems([]); setFilterRole(""); setFilterSeg(""); }

  // ── Dates ─────────────────────────────────────────────────────────────────
  const [effectiveDate, setEffectiveDate] = useState(period?.period_start_date ?? "");
  const [endDate,       setEndDate]       = useState("");
  const planEffectiveFrom = selectedPlan?.temporalRelationship?.effectiveFrom ?? null;
  const dateWarning = planEffectiveFrom && effectiveDate && effectiveDate < planEffectiveFrom
    ? `Plan effective from ${planEffectiveFrom}. Assignment date is earlier.` : null;

  // ── Analyze fetch — scoped to selected plan ───────────────────────────────
  const analyzeUrl = selectedPlan && period
    ? `${API_BASE}/plan-assignments/analyze?fiscal_period_id=${period.fiscal_period_id}&plan_id=${selectedPlan.planId}&page=0&page_size=500`
    : null;
  const { data: analyzeData, loading: analyzeLoading } = useFetch(analyzeUrl);
  const allPositions = analyzeData?.positions ?? [];
  const summary      = analyzeData?.summary   ?? {};

  // Reset shuttle when plan changes
  useEffect(() => { setRightItems([]); setLeftChecked(new Set()); }, [analyzeUrl]);

  // Filter options derived from analyze population (never from full org)
  const buOptions      = useMemo(() => [...new Set(allPositions.map((p) => p.bu_code).filter(Boolean))].sort(), [allPositions]);
  const roleOptions    = useMemo(() => [...new Set(allPositions.map((p) => p.role_code).filter(Boolean))].sort(), [allPositions]);
  const segOptions     = useMemo(() => [...new Set(allPositions.map((p) => p.segment_code).filter(Boolean))].sort(), [allPositions]);
  const motionOptions  = useMemo(() => [...new Set(allPositions.map((p) => p.selling_motion_code).filter(Boolean))].sort(), [allPositions]);

  // Position lookup map
  const posById = useMemo(() => {
    const m = {};
    allPositions.forEach((p) => { m[p.position_id] = p; });
    return m;
  }, [allPositions]);

  // ── Shuttle state ─────────────────────────────────────────────────────────
  const [rightItems,   setRightItems]   = useState([]);   // positions in selected pane
  const [leftChecked,  setLeftChecked]  = useState(new Set()); // checked in left pane
  const [rightChecked, setRightChecked] = useState(new Set()); // checked in right pane
  const [rightSearch,  setRightSearch]  = useState("");

  const rightIds = useMemo(() => new Set(rightItems.map((p) => p.position_id)), [rightItems]);

  // Left pane: available positions (not in right) after filters.
  // Role/Segment filtering uses role_code/segment_code from analyze response.
  // The filter bar Role/Segment selects show display names pre-plan-selection (from plan registry)
  // and role_code/segment_code post-plan-selection (from analyze). After plan selected,
  // filterRole holds the roleName that was used to narrow the plan choice — we translate
  // it to matching role_codes from allPositions. Since analyze scopes by plan's role+segment,
  // all positions will match anyway; the filter mainly serves the multi-plan-component case.
  const posRoleCodes = useMemo(
    () => new Set(allPositions.map((p) => p.role_code).filter(Boolean)),
    [allPositions]
  );
  const posSegCodes = useMemo(
    () => new Set(allPositions.map((p) => p.segment_code).filter(Boolean)),
    [allPositions]
  );

  const leftFiltered = useMemo(() => {
    const q = posSearch.toLowerCase();
    // When filterRole is a display name (pre-analyze), we can't match role_codes — skip position filter.
    // When filterRole matches a role_code in the population, apply it.
    // Safe rule: only apply role filter to positions if the value looks like a role_code (no spaces).
    const applyRoleFilter  = filterRole  && posRoleCodes.has(filterRole);
    const applySegFilter   = filterSeg   && posSegCodes.has(filterSeg);

    return allPositions.filter((p) => {
      if (rightIds.has(p.position_id))                                  return false;
      if (activeOnly   && !p.is_active)                                 return false;
      if (filterBU     && p.bu_code             !== filterBU)           return false;
      if (applyRoleFilter  && p.role_code        !== filterRole)        return false;
      if (applySegFilter   && p.segment_code     !== filterSeg)         return false;
      if (filterMotion && p.selling_motion_code  !== filterMotion)      return false;
      if (q && !(
        (p.full_name   ?? "").toLowerCase().includes(q) ||
        (p.employee_id ?? "").toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [allPositions, rightIds, activeOnly, filterBU, filterRole, filterSeg, filterMotion, posSearch, posRoleCodes, posSegCodes]);

  // Group left pane
  const leftGrouped = useMemo(() => {
    if (groupMode === "flat") {
      return [{ key: "__all__", label: null, items: [...leftFiltered].sort((a, b) => a.full_name.localeCompare(b.full_name)) }];
    }
    if (groupMode === "role") {
      const groups = {};
      leftFiltered.forEach((p) => {
        const key = p.role_code ?? "__unknown__";
        if (!groups[key]) groups[key] = { key, label: p.role_code ?? "Unknown", items: [] };
        groups[key].items.push(p);
      });
      return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
    }
    // manager grouping (default)
    const allIds = new Set(allPositions.map((p) => p.position_id));
    const groups = {};
    const ungrouped = [];
    leftFiltered.forEach((p) => {
      const mgr = p.manager_position_id;
      if (mgr && allIds.has(mgr)) {
        if (!groups[mgr]) groups[mgr] = { key: mgr, label: posById[mgr]?.full_name ?? "Manager", items: [] };
        groups[mgr].items.push(p);
      } else {
        ungrouped.push(p);
      }
    });
    const result = Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
    if (ungrouped.length) result.push({ key: "__no_mgr__", label: "No Manager", items: ungrouped.sort((a, b) => a.full_name.localeCompare(b.full_name)) });
    return result;
  }, [leftFiltered, groupMode, allPositions, posById]);

  // Right pane filtered
  const rightFiltered = useMemo(() => {
    if (!rightSearch.trim()) return rightItems;
    const q = rightSearch.toLowerCase();
    return rightItems.filter((p) => p.full_name?.toLowerCase().includes(q) || p.employee_id?.toLowerCase().includes(q));
  }, [rightItems, rightSearch]);

  // ── Shuttle actions ───────────────────────────────────────────────────────
  function moveRight() {
    if (leftChecked.size === 0) return;
    const toMove = leftFiltered.filter((p) => leftChecked.has(p.position_id));
    setRightItems((prev) => {
      const existing = new Set(prev.map((p) => p.position_id));
      return [...prev, ...toMove.filter((p) => !existing.has(p.position_id))];
    });
    setLeftChecked(new Set());
  }
  function moveLeft() {
    if (rightChecked.size === 0) return;
    setRightItems((prev) => prev.filter((p) => !rightChecked.has(p.position_id)));
    setRightChecked(new Set());
  }
  function toggleLeftCheck(id) {
    setLeftChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleRightCheck(id) {
    setRightChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleGroupCheck(group) {
    const ids = group.items.map((p) => p.position_id);
    const allChecked = ids.every((id) => leftChecked.has(id));
    setLeftChecked((prev) => { const n = new Set(prev); allChecked ? ids.forEach((id) => n.delete(id)) : ids.forEach((id) => n.add(id)); return n; });
  }
  function clearRight() { setRightItems([]); setRightChecked(new Set()); }

  // ── Decision gate ─────────────────────────────────────────────────────────
  const [gateState,    setGateState]    = useState("IDLE");
  const [dryResult,    setDryResult]    = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [gateError,    setGateError]    = useState("");

  function resetGate() { setGateState("IDLE"); setDryResult(null); setCommitResult(null); setGateError(""); }

  function buildRows() {
    return rightItems.map((pos) => ({
      employee_id:        pos.employee_id,
      plan_code:          selectedPlan.planCode,
      fiscal_period_code: period.period_code,
      business_unit_code: pos.bu_code ?? "DEFAULT_BU",
      effective_date:     effectiveDate,
      end_date:           endDate || null,
    }));
  }

  async function handlePreview() {
    if (!selectedPlan || rightItems.length === 0) return;
    setGateState("PREVIEWING"); setGateError("");
    try {
      const res = await fetch(`${API_BASE}/plan-assignments/bulk-import-json?dry_run=true&force=true`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: buildRows() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setGateError(json?.detail?.error?.message ?? `Error ${res.status}`); setGateState("IDLE"); return; }
      setDryResult(json);
    } catch (e) { setGateError(e.message || "Preview failed."); setGateState("IDLE"); }
  }

  async function handleCommit(force) {
    if (!selectedPlan || rightItems.length === 0) return;
    setGateState("COMMITTING");
    try {
      const res = await fetch(`${API_BASE}/plan-assignments/bulk-import-json?dry_run=false&force=${force}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: buildRows() }),
      });
      const json = await res.json().catch(() => ({}));
      setCommitResult(json);
      setGateState("DONE");
      onSuccess?.();
    } catch (e) { setGateError(e.message || "Commit failed."); setGateState("IDLE"); }
  }

  // ── Locked period guard ───────────────────────────────────────────────────
  if (periodLocked) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
        <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
        <p className="text-tok-sm text-[rgb(var(--color-warning))]">
          Period is <strong>{period?.period_status ?? "not open"}</strong> — position assignment is not available.
        </p>
      </div>
    );
  }

  // Done state
  if (gateState === "DONE" && commitResult) {
    return (
      <AssignmentResultCard
        result={commitResult}
        onReset={() => { resetGate(); setRightItems([]); setLeftChecked(new Set()); }}
      />
    );
  }

  const previewEnabled = rightItems.length > 0 && !!effectiveDate && !!selectedPlan;

  return (
    <div className="space-y-4">

      {/* ── Filter bar — ALWAYS VISIBLE, filters both plan list and position list ── */}
      {/* BU/Role/Segment here narrow the plan dropdown before selection.           */}
      {/* After plan selected, same filters narrow the position list in the shuttle. */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1.5 text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide shrink-0 mr-1">
            <Filter className="h-3 w-3" /> Filter
          </div>
          {/* BU — narrows plan list to plans with matching BU, then narrows position list */}
          {buOptions.length > 0 && (
            <select value={filterBU} onChange={(e) => setFilterBU(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))]">
              <option value="">All BUs</option>
              {buOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          {/* Role — pre-plan: shows roleName to narrow plan list; post-plan: shows role_code to narrow position list */}
          <select value={filterRole} onChange={(e) => { setFilterRole(e.target.value); setSelectedPlan(null); resetGate(); setRightItems([]); }}
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))]">
            <option value="">All Roles</option>
            {(allPositions.length > 0 ? roleOptions : planRoleOptions).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {/* Segment — pre-plan: shows segmentName to narrow plan list; post-plan: shows segment_code to narrow position list */}
          <select value={filterSeg} onChange={(e) => { setFilterSeg(e.target.value); setSelectedPlan(null); resetGate(); setRightItems([]); }}
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))]">
            <option value="">All Segments</option>
            {(allPositions.length > 0 ? segOptions : planSegOptions).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {/* Selling Motion — position list only (resolved after analyze) */}
          {motionOptions.length > 0 && (
            <select value={filterMotion} onChange={(e) => setFilterMotion(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))]">
              <option value="">All Motions</option>
              {motionOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          {/* Active only */}
          <label className="flex items-center gap-1.5 cursor-pointer ml-1">
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
              className="rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]" />
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Active only</span>
          </label>
          {/* Group by */}
          <select value={groupMode} onChange={(e) => setGroupMode(e.target.value)}
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))]">
            <option value="manager">By Manager</option>
            <option value="role">By Role</option>
            <option value="flat">Flat List</option>
          </select>
          {hasFilters && <button className="text-button text-tok-xs" onClick={clearFilters}>Clear</button>}
        </div>
      </div>

      {/* ── Plan + dates bar ── */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4">
        <div className="flex flex-wrap gap-4 items-end">

          {/* Plan searchable dropdown — pre-filtered by Role/Segment selected above */}
          <div className="flex-1 min-w-64" ref={planDropRef}>
            <label className="block text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">
              Plan <span className="normal-case font-normal">— {filteredPlans.length} approved</span>
            </label>
            <div className="relative">
              <div className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 hover:border-[rgb(var(--color-primary))]/50 transition-colors cursor-pointer"
                onClick={() => { setPlanDropOpen(true); if (selectedPlan) { setPlanSearch(""); setSelectedPlan(null); resetGate(); setRightItems([]); } }}>
                <Search className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))] shrink-0" />
                <input type="text"
                  placeholder={`Search ${filteredPlans.length} plan${filteredPlans.length !== 1 ? "s" : ""}…`}
                  value={selectedPlan ? `${selectedPlan.planName} · ${selectedPlan.planCode}` : planSearch}
                  onChange={(e) => { setPlanSearch(e.target.value); setSelectedPlan(null); setPlanDropOpen(true); }}
                  onFocus={() => { setSelectedPlan(null); setPlanDropOpen(true); }}
                  className="flex-1 bg-transparent text-tok-sm text-[rgb(var(--color-text))] outline-none placeholder:italic placeholder:text-[rgb(var(--color-text-muted))]"
                />
                {selectedPlan
                  ? <button onClick={(e) => { e.stopPropagation(); setSelectedPlan(null); setPlanSearch(""); resetGate(); setRightItems([]); }} className="shrink-0"><X className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" /></button>
                  : <ChevronDown className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))] shrink-0" />
                }
              </div>
              {planDropOpen && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-xl">
                  {filteredPlans.length === 0
                    ? <p className="px-3 py-3 text-tok-xs text-[rgb(var(--color-text-muted))] italic">No plans match filters</p>
                    : filteredPlans.map((p) => (
                      <div key={p.planId}
                        className={"px-3 py-2.5 cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))] last:border-0 " + (selectedPlan?.planId === p.planId ? "bg-[rgb(var(--color-primary))]/8" : "")}
                        onClick={() => selectPlan(p)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{p.planName}</p>
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-mono">{p.planCode}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{p.segmentName}</p>
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{p.fiscalPeriodLabel}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          </div>

          {/* Effective date */}
          <div>
            <label className="block text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">Effective Date *</label>
            <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
              className={"rounded-lg border px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none bg-[rgb(var(--color-surface-muted))] focus:border-[rgb(var(--color-primary))] " + (dateWarning ? "border-[rgb(var(--color-warning))]" : "border-[rgb(var(--color-border))]")} />
            {dateWarning && <p className="text-tok-xs text-[rgb(var(--color-warning))] mt-0.5">{dateWarning}</p>}
          </div>

          {/* End date */}
          <div>
            <label className="block text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">
              End Date <span className="normal-case font-normal">(optional)</span>
            </label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))]" />
          </div>
        </div>
      </div>

      {/* ── Summary pills (shown after plan selected) ── */}
      {selectedPlan && (
        <div className="flex flex-wrap gap-2 items-center">
          {analyzeLoading ? (
            <div className="flex items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]"><Spinner size={3} /> Analyzing population…</div>
          ) : (
            [
              ["Matched",          summary.matched,          "text-[rgb(var(--color-text))]"],
              ["Ready",            summary.ready,            "text-[rgb(var(--color-success))]"],
              ["Already Assigned", summary.already_assigned, "text-[rgb(var(--color-warning))]"],
              ["Inactive",         summary.inactive,         "text-[rgb(var(--color-text-muted))]"],
              ["Pending Hire",     summary.pending_hire,     "text-[rgb(var(--color-text-muted))]"],
              ["Needs Review",     summary.needs_review,     "text-[rgb(var(--color-warning))]"],
            ].filter(([, v]) => v > 0).map(([label, value, cls]) => (
              <div key={label} className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2.5 py-1">
                <span className={"text-tok-sm font-semibold " + cls}>{value}</span>
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── No plan selected placeholder ── */}
      {!selectedPlan && (
        <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-10 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Select a plan above to see the eligible population</p>
        </div>
      )}

      {/* ── Shuttle (shown after plan + positions loaded) ── */}
      {selectedPlan && !analyzeLoading && allPositions.length > 0 && (
        <div className="space-y-3">

          {/* Shuttle */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-0 rounded-xl border border-[rgb(var(--color-border))] overflow-hidden" style={{ minHeight: 420 }}>

            {/* Left pane — available */}
            <div className="flex flex-col border-r border-[rgb(var(--color-border))]">
              <div className="px-3 pt-3 pb-2 border-b border-[rgb(var(--color-border))] space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                    Available <span className="font-normal normal-case ml-1 text-[rgb(var(--color-text-muted))]">{leftFiltered.length}</span>
                  </p>
                  {leftChecked.size > 0 && <span className="text-tok-xs text-[rgb(var(--color-primary))]">{leftChecked.size} checked</span>}
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                  <input type="text" placeholder="Search name or ID…" value={posSearch} onChange={(e) => setPosSearch(e.target.value)}
                    className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] pl-8 pr-3 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none" />
                </div>
              </div>
              <div className="overflow-y-auto flex-1" style={{ maxHeight: 400 }}>
                {leftGrouped.length === 0 ? (
                  <p className="px-3 py-6 text-tok-xs text-[rgb(var(--color-text-muted))] text-center">
                    {allPositions.length === 0 ? "No positions in this plan's population." : "All positions moved to selected."}
                  </p>
                ) : leftGrouped.map((group) => (
                  <div key={group.key}>
                    {group.label && (
                      <div className="flex items-center gap-2 px-3 py-1.5 sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]/50">
                        <input type="checkbox"
                          checked={group.items.length > 0 && group.items.every((p) => leftChecked.has(p.position_id))}
                          ref={(el) => { if (el) el.indeterminate = group.items.some((p) => leftChecked.has(p.position_id)) && !group.items.every((p) => leftChecked.has(p.position_id)); }}
                          onChange={() => toggleGroupCheck(group)}
                          className="h-3.5 w-3.5 accent-[rgb(var(--color-primary))] shrink-0 rounded" />
                        <span className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] flex-1 truncate">{group.label}</span>
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))] shrink-0">{group.items.length}</span>
                      </div>
                    )}
                    {group.items.map((pos) => {
                      const isAssigned = pos.assignment_status === "already_assigned";
                      return (
                        <div key={pos.position_id}
                          onClick={() => toggleLeftCheck(pos.position_id)}
                          className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer border-b border-[rgb(var(--color-border))]/30 transition-colors ${
                            leftChecked.has(pos.position_id) ? "bg-[rgb(var(--color-primary))]/6" : "hover:bg-[rgb(var(--color-surface-muted))]"
                          }`}
                        >
                          <input type="checkbox" checked={leftChecked.has(pos.position_id)} onChange={() => toggleLeftCheck(pos.position_id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[rgb(var(--color-primary))] rounded" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-tok-xs font-medium text-[rgb(var(--color-text))] truncate">{pos.full_name}</span>
                              {isAssigned && <AlertTriangle className="h-3 w-3 shrink-0 text-[rgb(var(--color-warning))]" title={`On: ${pos.current_plan_code}`} />}
                            </div>
                            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                              {[pos.employee_id, pos.role_code, pos.segment_code].filter(Boolean).join(" · ")}
                            </span>
                            {isAssigned && pos.current_plan_code && (
                              <p className="text-tok-xs text-[rgb(var(--color-warning))]">On: {pos.current_plan_code}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 border-t border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{leftFiltered.length} available</span>
              </div>
            </div>

            {/* Shuttle controls */}
            <div className="flex flex-col items-center justify-center gap-2 px-3 bg-[rgb(var(--color-surface-muted))]/40">
              <button onClick={moveRight} disabled={leftChecked.size === 0} title="Move checked to selected"
                className="flex items-center justify-center h-8 w-8 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/10 disabled:opacity-30 disabled:cursor-not-allowed transition">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button onClick={moveLeft} disabled={rightChecked.size === 0} title="Return checked to available"
                className="flex items-center justify-center h-8 w-8 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-30 disabled:cursor-not-allowed transition">
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>

            {/* Right pane — selected */}
            <div className="flex flex-col border-l border-[rgb(var(--color-border))]">
              <div className="px-3 pt-3 pb-2 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-primary))]/5 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-primary))]">
                    Selected <span className="font-normal normal-case ml-1">{rightItems.length}</span>
                  </p>
                  {rightChecked.size > 0 && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rightChecked.size} checked</span>}
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                  <input type="text" placeholder="Search…" value={rightSearch} onChange={(e) => setRightSearch(e.target.value)}
                    className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] pl-8 pr-3 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none" />
                </div>
              </div>
              <div className="overflow-y-auto flex-1" style={{ maxHeight: 400 }}>
                {rightFiltered.length === 0 ? (
                  <p className="px-3 py-6 text-tok-xs text-[rgb(var(--color-text-muted))] text-center">
                    {rightItems.length === 0 ? "Check positions on the left and click › to add." : "No matches."}
                  </p>
                ) : rightFiltered.map((pos) => {
                  const isAssigned = pos.assignment_status === "already_assigned";
                  return (
                    <div key={pos.position_id}
                      onClick={() => toggleRightCheck(pos.position_id)}
                      className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer border-b border-[rgb(var(--color-border))]/30 transition-colors ${
                        rightChecked.has(pos.position_id) ? "bg-[rgb(var(--color-primary))]/6" : "hover:bg-[rgb(var(--color-surface-muted))]"
                      }`}
                    >
                      <input type="checkbox" checked={rightChecked.has(pos.position_id)} onChange={() => toggleRightCheck(pos.position_id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[rgb(var(--color-primary))] rounded" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-tok-xs font-medium text-[rgb(var(--color-text))] truncate">{pos.full_name}</span>
                          {isAssigned && <AlertTriangle className="h-3 w-3 shrink-0 text-[rgb(var(--color-warning))]" />}
                        </div>
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {[pos.employee_id, pos.role_code, pos.segment_code].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setRightItems((prev) => prev.filter((p) => p.position_id !== pos.position_id)); }}
                        className="shrink-0 rounded p-0.5 hover:bg-[rgb(var(--color-surface-muted))] mt-0.5">
                        <X className="h-3 w-3 text-[rgb(var(--color-text-muted))]" />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="px-3 py-2 border-t border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] flex items-center justify-between">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rightItems.length} selected</span>
                {rightItems.length > 0 && (
                  <button onClick={clearRight} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-danger))] transition">Clear all</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gate error */}
      {gateError && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{gateError}</div>
      )}

      {/* Decision gate */}
      {gateState === "PREVIEWING" && dryResult && (
        <AssignmentDecisionGate
          summary={dryResult}
          onOptionA={() => handleCommit(false)}
          onOptionB={() => handleCommit(true)}
          onCancel={resetGate}
          committing={gateState === "COMMITTING"}
        />
      )}

      {/* Footer — Preview button */}
      {selectedPlan && rightItems.length > 0 && gateState === "IDLE" && (
        <div className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            <strong className="text-[rgb(var(--color-text))]">{rightItems.length}</strong> position{rightItems.length !== 1 ? "s" : ""} selected
            {!effectiveDate && <span className="ml-2 text-[rgb(var(--color-danger))]">— Effective date required</span>}
          </p>
          <button className="primary-button" onClick={handlePreview} disabled={!previewEnabled}>
            Preview Assignment <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}


// ── AssignmentTable ───────────────────────────────────────────────────────────
// Current assignments table. Sortable, searchable. Click row → detail drawer.
// S834 Step 3: role_code column (OI-S833-ROLE-DISPLAY-GET resolved).

function AssignmentTable({ assignments, loading, error, periodId, statusTone }) {
  const [search,   setSearch]   = useState("");
  const [sortCol,  setSortCol]  = useState("plan_code");
  const [sortDir,  setSortDir]  = useState("asc");
  const [selected, setSelected] = useState(null);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (assignments ?? []).filter((a) =>
      !q ||
      (a.plan_code ?? a.plan_name ?? "").toLowerCase().includes(q) ||
      (a.role_code ?? a.role_name ?? a.segment_name ?? "").toLowerCase().includes(q) ||
      (a.assignment_status ?? "").toLowerCase().includes(q)
    );
  }, [assignments, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "plan_code")           { av = a.plan_code ?? ""; bv = b.plan_code ?? ""; }
      else if (sortCol === "role")           { av = a.role_code ?? a.role_name ?? a.segment_name ?? ""; bv = b.role_code ?? b.role_name ?? b.segment_name ?? ""; }
      else if (sortCol === "effective_date") { av = a.effective_date ?? ""; bv = b.effective_date ?? ""; }
      else if (sortCol === "status")         { av = a.assignment_status ?? ""; bv = b.assignment_status ?? ""; }
      else { av = ""; bv = ""; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  return (
    <Card title="Current Assignments" action={assignments?.length > 0 ? <Pill tone="Ready">{assignments.length} assignments</Pill> : null}>
      {!periodId && <ErrorNote message="No open fiscal period — cannot load assignments." />}
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load plan assignments." />}
      {!loading && !error && periodId && (assignments ?? []).length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No plan assignments for this period.</p>
      )}
      {!loading && !error && (assignments ?? []).length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search assignments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {assignments.length}</span>}
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                <tr>
                  {[["plan_code","Plan"],["role","Role"],["effective_date","Effective Date"],["status","Status"]].map(([col, label]) => (
                    <th key={col} className={thCls()} onClick={() => toggleSort(col)}>
                      {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => (
                  <tr
                    key={a.plan_assignment_id}
                    className={"border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] " + (selected?.plan_assignment_id === a.plan_assignment_id ? "bg-[rgb(var(--color-surface-muted))]" : "")}
                    onClick={() => setSelected(selected?.plan_assignment_id === a.plan_assignment_id ? null : a)}
                  >
                    <td className={tdCls()}>{a.plan_code ?? "—"}</td>
                    <td className={tdCls()}>{a.role_code ?? a.role_name ?? a.segment_name ?? "—"}</td>
                    <td className={tdCls()}>{a.effective_date ?? "—"}</td>
                    <td className={tdCls()}><Pill tone={statusTone[a.assignment_status] ?? "Draft"}>{a.assignment_status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selected && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSelected(null)} />
              <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
                <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
                  <div>
                    <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Assignment Detail</p>
                    <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">{selected.plan_code}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="ml-2 rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {[
                    ["Plan",           selected.plan_code],
                    ["Role",           selected.role_code ?? selected.role_name],
                    ["Segment",        selected.segment_name],
                    ["Effective Date", selected.effective_date],
                    ["End Date",       selected.end_date],
                    ["Status",         selected.assignment_status],
                    ["Business Unit",  selected.bu_code],
                    ["Period",         selected.fiscal_period_code],
                  ].map(([label, val]) => val != null && (
                    <div key={label}>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                      <p className="text-tok-sm text-[rgb(var(--color-text))]">{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}


// ── PlanAssignmentPanel ──────────────────────────────────────────────────────
// Render order (spec §16 + §9 + §6):
//   1. Shuttle workspace (position picker) or CSV import — tab-switched
//   2. CoverageView — variable comp budget by sales motion
//   3. PopulationAnalysisPanel — matched/assigned/ready population breakdown
//   4. ConflictDetectionPanel — on-demand 15-category conflict check
//   5. AssignmentTable — current assignments (always visible)

function PlanAssignmentPanel({ mode, period }) {
  const periodId = period?.fiscal_period_id;
  const [activeTab, setActiveTab] = useState("position");
  const [logsKey,   setLogsKey]   = useState(0);

  const tabs = [
    { key: "position", label: "Assign by Position" },
    { key: "csv",      label: "Assign by CSV" },
  ];

  return (
    <div className="w-full space-y-6">

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))] pb-0">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={
              "px-4 py-2 text-tok-sm font-medium rounded-t-lg border border-b-0 transition-colors " +
              (activeTab === t.key
                ? "bg-[rgb(var(--color-surface))] border-[rgb(var(--color-border))] text-[rgb(var(--color-primary))]"
                : "bg-transparent border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Assignment surface — shuttle picker or CSV */}
      {activeTab === "position" && (
        <AssignmentPositionPicker period={period} onSuccess={() => setLogsKey((k) => k + 1)} />
      )}
      {activeTab === "csv" && (
        <Card title="CSV Import">
          <AssignmentCsvImport period={period} onSuccess={() => setLogsKey((k) => k + 1)} />
        </Card>
      )}

      {/* Population Analysis — matched/assigned/ready breakdown (§6) */}
      <PopulationAnalysisPanel fiscalPeriodId={periodId} />

      {/* Conflict Detection — on-demand 15-category check (§6) */}
      <ConflictDetectionPanel fiscalPeriodId={periodId} />
    </div>
  );
}

export default PlanAssignmentPanel;
